import { defineStore } from 'pinia'

import type { StudioBroadcast } from '@/types'
import { fetchStudioBroadcasts, markStudioBroadcastAsRead } from '../api'

interface StudioBroadcastState {
  unread: StudioBroadcast[]
  loading: boolean
  loadedStudioId: number | null
}

const sortByPublishedDesc = (items: StudioBroadcast[]): StudioBroadcast[] =>
  [...items].sort((a, b) => {
    const aMs = Date.parse(a.published_at ?? '')
    const bMs = Date.parse(b.published_at ?? '')
    return (Number.isNaN(bMs) ? 0 : bMs) - (Number.isNaN(aMs) ? 0 : aMs)
  })

export const useStudioBroadcastsStore = defineStore('studio-broadcasts', {
  state: (): StudioBroadcastState => ({
    unread: [],
    loading: false,
    loadedStudioId: null,
  }),
  getters: {
    latestUnread: (state): StudioBroadcast | null => state.unread[0] ?? null,
    unreadCount: (state): number => state.unread.length,
  },
  actions: {
    async loadUnreadOnce(studioId: number | null | undefined) {
      if (!studioId || !Number.isFinite(studioId)) {
        this.unread = []
        this.loadedStudioId = null
        return
      }
      if (this.loadedStudioId === studioId) return

      this.loading = true
      try {
        const broadcasts = await fetchStudioBroadcasts()
        this.unread = sortByPublishedDesc(broadcasts.filter((item) => !item.read))
        this.loadedStudioId = studioId
      } catch {
        this.unread = []
      } finally {
        this.loading = false
      }
    },
    dismissLocally(id: number) {
      this.unread = this.unread.filter((item) => item.id !== id)
    },
    async markRead(id: number) {
      try {
        await markStudioBroadcastAsRead(id)
      } catch {
        // Evitar bloquear el flujo visual por un fallo temporal.
      } finally {
        this.dismissLocally(id)
      }
    },
    async markAllRead() {
      const ids = this.unread.map((item) => item.id)
      if (!ids.length) return
      await Promise.all(ids.map((id) => this.markRead(id)))
    },
    resetForStudio() {
      this.unread = []
      this.loadedStudioId = null
    },
  },
})
